import { fetchApp } from "../../services/apiService";
export type ResellerStatus = "ACTIVE" | "OVERDUE" | "SUSPENDED" | "INACTIVE";

export type ResellerAccountStatus =
  | "AVAILABLE"
  | "CONNECTED"
  | "DISABLED"
  | "REMOVED";

export interface ResellerPartnerSummary {
  id: string;
  client_id: string;

  business_name: string;
  contact_person?: string | null;
  phone_number: string;
  email?: string | null;
  location?: string | null;

  account_number: string;
  username_prefix: string;

  billing_started_at?: string | null;
  billing_expires_at?: string | null;
  payment_due_at?: string | null;

  grace_period_days: number;
  grace_ends_at?: string | null;

  status: ResellerStatus;
  is_active: boolean;

  notes?: string | null;

  total_accounts: number;
  available_accounts: number;
  connected_accounts: number;
  disabled_accounts: number;
  removed_accounts: number;

  total_bill: number;
  total_paid: number;
  balance: number;

  created_at?: string | null;
  updated_at?: string | null;
}

export interface ResellerPartnerAccount {
  id: string;
  client_id: string;
  reseller_partner_id: string;
  batch_id?: string | null;

  router_id: string;
  router_name?: string | null;

  plan_id: string;
  plan_name?: string | null;

  username: string;
  password: string;

  amount_payable: number;
  status: ResellerAccountStatus;

  customer_id?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;

  secret_id?: string | null;

  connected_at?: string | null;
  synced_at?: string | null;

  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ResellerPayment {
  id: string;
  client_id: string;
  reseller_partner_id: string;

  amount: number;

  payment_method?: string | null;
  payment_reference?: string | null;

  paid_at?: string | null;
  received_by?: string | null;

  notes?: string | null;

  created_at?: string | null;
}

export interface ResellerPartnerDetail extends ResellerPartnerSummary {
  accounts: ResellerPartnerAccount[];
  payments: ResellerPayment[];
}

export interface CreateResellerPayload {
  business_name: string;
  contact_person?: string;
  phone_number: string;
  email?: string;
  location?: string;

  account_number?: string;
  username_prefix?: string;

  billing_started_at?: string;
  billing_expires_at?: string;
  payment_due_at?: string;

  grace_period_days: number;
  notes?: string;
}

export interface GenerateAccountsPayload {
  router_id: string;
  username_prefix?: string;
  plans: {
    plan_id: string;
    count: number;
    amount_payable?: number;
  }[];
}

export interface RecordPaymentPayload {
  amount: number;
  payment_method?: string;
  payment_reference?: string;
  paid_at?: string;
  notes?: string;
}

function buildQuery(params?: Record<string, string | undefined | null>) {
  if (!params) return "";

  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.append(key, value);
    }
  });

  const queryString = query.toString();

  return queryString ? `?${queryString}` : "";
}

export async function getResellerPartners(params?: {
  search?: string;
  status?: string;
}) {
  const query = buildQuery(params);

  return fetchApp<ResellerPartnerSummary[]>(`/reseller-partners/${query}`);
}

export async function createResellerPartner(payload: CreateResellerPayload) {
  return fetchApp<ResellerPartnerSummary>("/reseller-partners/", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getResellerPartnerDetail(id: string) {
  return fetchApp<ResellerPartnerDetail>(`/reseller-partners/${id}`);
}

export async function updateResellerPartner(
  id: string,
  payload: Partial<CreateResellerPayload>,
) {
  return fetchApp<ResellerPartnerSummary>(`/reseller-partners/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function generateResellerAccounts(
  id: string,
  payload: GenerateAccountsPayload,
) {
  return fetchApp<{
    reseller_partner: ResellerPartnerSummary;
    batch: {
      id: string;
      client_id: string;
      reseller_partner_id: string;
      router_id: string;
      username_prefix: string;
      total_accounts: number;
      created_by?: string | null;
      created_at?: string | null;
    };
    accounts: ResellerPartnerAccount[];
  }>(`/reseller-partners/${id}/generate-accounts`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function recordResellerPayment(
  id: string,
  payload: RecordPaymentPayload,
) {
  return fetchApp<ResellerPayment>(`/reseller-partners/${id}/payments`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function deactivateResellerPartner(id: string) {
  return fetchApp<ResellerPartnerSummary>(
    `/reseller-partners/${id}/deactivate`,
    {
      method: "POST",
    },
  );
}

export async function activateResellerPartner(id: string) {
  return fetchApp<ResellerPartnerSummary>(`/reseller-partners/${id}/activate`, {
    method: "POST",
  });
}

export interface ConnectResellerAccountPayload {
  customer_name: string;
  phone_number: string;
  email?: string;
  location?: string;
  notes?: string;
}

export async function connectResellerAccountToCustomer(
  resellerId: string,
  accountId: string,
  payload: ConnectResellerAccountPayload,
) {
  return fetchApp(
    `/reseller-partners/${resellerId}/accounts/${accountId}/connect-customer`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}
